import React, { useState } from 'react';
import { AdminPaymentsAPI } from '../../services/api';
import { formatRand, apiErrorMessage } from '../charter/charterUtils';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none';

/**
 * "Resolve payment" for one stuck row (Paystack plan 3.7.9), super admins
 * only; the server refuses anyone else with 403 anyway. The admin checks the
 * Paystack dashboard first, then picks what happened, with a note. A
 * `capture_failed` capture needs the Paystack support ticket id; a refund
 * resolved "succeeded" needs its refund id from the dashboard, and a charge (a
 * tip or a seat charge) its transaction id (#583). A card check cannot be
 * resolved "succeeded": the server refuses it. The server
 * verifies once more and refuses if the answer is now definite. Its own
 * verify can see `capture_failed` when the stored one did not: a 400
 * `TICKET_REQUIRED` then shows the ticket id field, to fill and send again.
 */
const ResolveForm = ({ row, onDone, onCancel }) => {
  const [outcome, setOutcome] = useState('failed');
  const [note, setNote] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [refundId, setRefundId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ticketAsked, setTicketAsked] = useState(false);

  const needsTicket = row.captureFailed || ticketAsked;
  const needsRefundId = row.type === 'refund' && outcome === 'succeeded' && row.gateway === 'paystack';
  const needsTransactionId = Boolean(row.charge) && row.type !== 'card_check' && outcome === 'succeeded' && row.gateway === 'paystack';
  const ready = note.trim() && (!needsTicket || ticketId.trim()) && (!needsRefundId || refundId.trim()) && (!needsTransactionId || transactionId.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    if (!window.confirm(`Record ${row.reference} (${formatRand(row.amountCents)}) as ${outcome}?`)) return;
    setBusy(true);
    setError('');
    try {
      const body = { reference: row.reference, outcome, note: note.trim() };
      if (ticketId.trim()) body.ticketId = ticketId.trim();
      if (needsRefundId) body.refundId = refundId.trim();
      if (needsTransactionId) body.transactionId = transactionId.trim();
      const res = await AdminPaymentsAPI.resolve(body);
      onDone(`${row.reference}: ${res.data.data.outcome}.`);
    } catch (err) {
      if (err && err.response && err.response.data && err.response.data.code === 'TICKET_REQUIRED') setTicketAsked(true);
      setError(apiErrorMessage(err, 'That did not go through'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3" aria-label={`Resolve ${row.reference}`}>
      <p className="text-sm text-gray-700">
        Check this payment in the Paystack dashboard first. This records what you found, and settles the row once.
      </p>
      <fieldset className="flex gap-4 text-sm">
        <legend className="sr-only">What happened</legend>
        {[['succeeded', 'It went through'], ['failed', 'It did not go through']].map(([value, label]) => (
          <label key={value} className="flex items-center gap-2">
            <input type="radio" name={`outcome-${row.reference}`} value={value} checked={outcome === value} onChange={() => setOutcome(value)} />
            {label}
          </label>
        ))}
      </fieldset>
      <label className="block text-sm">
        <span className="text-gray-700">Note (what the dashboard shows)</span>
        <textarea className={inputClass} rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {needsTicket && (
        <label className="block text-sm">
          <span className="text-gray-700">Paystack support ticket id (capture_failed: resolve only on their written answer)</span>
          <input className={inputClass} value={ticketId} onChange={(e) => setTicketId(e.target.value)} />
        </label>
      )}
      {needsRefundId && (
        <label className="block text-sm">
          <span className="text-gray-700">Refund id from the Paystack dashboard</span>
          <input className={inputClass} value={refundId} onChange={(e) => setRefundId(e.target.value)} />
        </label>
      )}
      {needsTransactionId && (
        <label className="block text-sm">
          <span className="text-gray-700">Transaction id from the Paystack dashboard</span>
          <input className={inputClass} value={transactionId} onChange={(e) => setTransactionId(e.target.value)} />
        </label>
      )}
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={!ready || busy} className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy ? 'Resolving...' : 'Resolve payment'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ResolveForm;
